import { DatePipe } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Audit, Change } from 'src/app/models/audit.model';
import { isSuperAdmin } from 'src/app/auth/roles';
import { AuditService } from 'src/app/services/audit/audit.service';
import { DataPointService } from 'src/app/services/data-point/data-point.service';
import { ErrorHandlerService } from 'src/app/services/errorHandler/error-handler.service';
import { RefreshStateService } from 'src/app/services/refresh-state/refresh-state.service';

@Component({
	standalone: false,
	selector: 'app-audit',
	templateUrl: './audit.component.html',
	styleUrls: ['./audit.component.css']
})
export class AuditComponent implements OnDestroy {
	showDetails = false
	auditDetails: any = {}
	audits: Audit[] = []
	currentPage: number = 1
	pageSize = 7
	totalPages = 10
	loopArray = new Array(this.pageSize).fill(0).map((_, index) => index + 1)
	currentIncrement = this.pageSize
	selectedPage = {
		backgroundColor: "#0F2642",
		color: "white",
		borderRadius: "4px",
	}
	errorMessage = {
		success: true,
		message: ''
	}

	constructor(
		private auditService: AuditService, 
		private datePipe: DatePipe, 
		private errorService: ErrorHandlerService,
		private datapointService: DataPointService,
		private router: Router,
		private refreshState: RefreshStateService,
	) { }

	formatAction(eventDetails: string, performedOn: string): string {
		if (!performedOn) return eventDetails
		return eventDetails.replace(/^(Logsheet|Log Sheet|Form|Standard Form)(\s+)/i, `$1$2"${performedOn}" `)
	}

	stringToDate(date: string) {
		const newDate = new Date(date);
		const customFormat = 'dd-MMM-yyyy h:mma';
		const formattedDate = this.datePipe.transform(newDate, customFormat);
		return formattedDate;
	}
	differences: any[] = [];

	changeName(name: string) {
		if (name.includes('dataPoints[')) {
			let index = name.lastIndexOf('.'); // Find the index of the last dot
			if (index !== -1) { // Check if a dot was found
				let result = name.substring(index + 1); // Extract the part after the dot
				if (result == 'dataPointDescription') return 'DataPoint Description';
				if (result == 'dataPointName') return 'DataPoint Name';
			}
		}
		if (name == 'dataPointName' || name == 'companyName' || name == 'plantName' || name == 'unitName' || name == 'logSheetName' || name == 'dataPointTitle') return 'Name';
		if (name == 'companyAbbreviation') return 'Abbreviation';
		if (name == 'dataPointDescription') return 'Description';
		if (name == 'currentValue') return 'Value';
		if (name == 'type') return 'Duration';
		if (name == 'unitOfMeasurement') return 'Unit of Measurement';
		if (name == 'dataType') return 'Type';
		if (name == 'dataPointMax') return 'Max';
		if (name == 'dataPointMin') return 'Min';
		if (name == 'firstName') return 'First Name';
		if (name == 'lastName') return 'Last Name';
		if (name == 'email') return 'Email';
		if (name == 'datapointName') return name
		if (name == 'NotificationType') return 'Notification Type';
		if (name == 'NotificationChannels') return 'Notification Channels';
		if (name == 'RecipientType') return 'Recipient Type';
		if (name == 'CsvFileName') return 'CSV File Name';
		if (name == 'RecipientCount') return 'Recipient Count';

		else {
			return name;
		}
	}

	getDatapointName(id: number) {
		this
	}

	private formatAuditValue(value: any): string {
		if (value === null || value === undefined || value === '') {
			return '-'
		}
		return String(value)
	}

	private toAuditStateEntries(obj: any): { key: string; value: any }[] | null {
		if (!Array.isArray(obj)) return null

		const entries = obj.map((item: any) => {
			const key = item?.key ?? item?.Key
			const hasValue =
				Object.prototype.hasOwnProperty.call(item ?? {}, 'value') ||
				Object.prototype.hasOwnProperty.call(item ?? {}, 'Value')

			if (!key || !hasValue) return null

			return {
				key: String(key),
				value: item?.value ?? item?.Value,
			}
		})

		if (entries.some(entry => entry === null)) return null

		return entries as { key: string; value: any }[]
	}

	private findAuditEntryDifferences(previousObject: any, nextObject: any): any[] | null {
		const previousEntries = this.toAuditStateEntries(previousObject)
		const nextEntries = this.toAuditStateEntries(nextObject)

		if (!previousEntries && !nextEntries) return null

		const previousMap = new Map(
			(previousEntries ?? []).map(entry => [entry.key, this.formatAuditValue(entry.value)])
		)
		const nextMap = new Map(
			(nextEntries ?? []).map(entry => [entry.key, this.formatAuditValue(entry.value)])
		)
		const keys: string[] = []

		for (const entry of [...(previousEntries ?? []), ...(nextEntries ?? [])]) {
			if (!keys.includes(entry.key)) {
				keys.push(entry.key)
			}
		}

		return keys
			.map(key => ({
				key,
				oldValue: previousMap.get(key) ?? '-',
				newValue: nextMap.get(key) ?? '-',
			}))
			.filter(change => change.oldValue !== change.newValue)
	}

	findChangedValues(previousObject: any, nextObject: any) {
		const differencesInValues: any[] = [];

		const auditEntryDifferences = this.findAuditEntryDifferences(previousObject, nextObject);
		if (auditEntryDifferences !== null) {
			this.differences = auditEntryDifferences;
			return;
		}

		// Check if this is a notification configuration change
		const isNotificationConfig = previousObject && nextObject && 
			(previousObject[0]?.NotificationType || nextObject[0]?.NotificationType);

		if (isNotificationConfig && Array.isArray(previousObject) && Array.isArray(nextObject)) {
			// Handle notification configuration array changes
			// For each notification type, create two entries: one for channels, one for recipients
			for (let i = 0; i < Math.max(previousObject.length, nextObject.length); i++) {
				const prevConfig = previousObject[i];
				const nextConfig = nextObject[i];
				const notificationType = nextConfig?.NotificationType || prevConfig?.NotificationType;

				if (!notificationType) continue;

				// Entry 1: Channels - only add if different
				const prevChannels = prevConfig?.NotificationChannels || '-';
				const nextChannels = nextConfig?.NotificationChannels || '-';
				if (prevChannels !== nextChannels) {
					differencesInValues.push({
						key: `${notificationType} Channel`,
						oldValue: prevChannels,
						newValue: nextChannels
					});
				}

				// Entry 2: Recipients - only add if different
				const prevRecipients = prevConfig?.Recipients || '-';
				const nextRecipients = nextConfig?.Recipients || '-';
				if (prevRecipients !== nextRecipients) {
					differencesInValues.push({
						key: `${notificationType} Recipient(s)`,
						oldValue: prevRecipients,
						newValue: nextRecipients
					});
				}
			}

			this.differences = differencesInValues;
			return;
		}

		const previousKeys = this.getAllKeys(previousObject);
		const nextKeys = this.getAllKeys(nextObject);

		const similarKeys = previousKeys.filter(key => nextKeys.includes(key));
		const deletedKeys = previousKeys.filter(key => !nextKeys.includes(key));
		const addedKeys = nextKeys.filter(key => !previousKeys.includes(key));

		for (let key of deletedKeys) {
			const previousValue = this.readKey(previousObject, key);
			// Skip object types, IDs, and empty values
			if (typeof previousValue === "object" || key.includes('Id') || key.includes('orderOnSheet') || previousValue == '') {
				continue;
			}
			differencesInValues.push({
				key: this.changeName(key),
				oldValue: previousValue,
				newValue: '-'
			});
		}

		for (let key of addedKeys) {
			// Skip datapoint-specific 'value' logic and IDs
			if (key.includes('Id') || key.includes('orderOnSheet') || key.includes('dataPointName')) {
				continue
			}
			const nextValue = this.readKey(nextObject, key);
			if (typeof nextValue === "object" || nextValue == '') {
				continue;
			}

			if(key.includes('valueDataPoint')) {
				differencesInValues.push({
					key: "DataPoint Name",
					oldValue: '-',
					newValue: nextValue
				});
				continue;
			}

			differencesInValues.push({
				key: this.changeName(key),
				oldValue: '-',
				newValue: nextValue
			});
		}

		for (let key of similarKeys) {
			const previousValue = this.readKey(previousObject, key);
			const nextValue = this.readKey(nextObject, key);
			// Skip object types and IDs
			if (typeof previousValue === "object" || typeof nextValue === "object" || key.includes('Id') || key.includes('orderOnSheet')) {
				continue;
			}
			if (previousValue !== nextValue) {
				differencesInValues.push({
					key: this.changeName(key),
					oldValue: previousValue,
					newValue: nextValue
				});
			}
		}

		this.differences = differencesInValues;

	}

	showAuditDetails(audit: Audit) {
		this.showDetails = true
		this.auditDetails = audit
		this.persistRefreshState()

		if (this.isWorkflowAudit(audit)) {
			this.differences = this.findWorkflowChangedValues(
				audit.prevStateObj,
				audit.currentStateObj
			)
			return
		}

		this.findChangedValues(
			audit.prevStateObj,
			audit.currentStateObj
		)
	}

	private isWorkflowAudit(audit: Audit): boolean {
		const eventDetails = audit.eventDetails?.toLowerCase() ?? ''
		return eventDetails.includes('workflow template') ||
			this.isWorkflowAuditState(audit.prevStateObj) ||
			this.isWorkflowAuditState(audit.currentStateObj)
	}

	private isWorkflowAuditState(state: any): boolean {
		if (!state || typeof state !== 'object' || Array.isArray(state)) return false

		return Boolean(
			state.Definition ||
			state.definition ||
			state.StageCount !== undefined ||
			state.stageCount !== undefined ||
			(state.Sku !== undefined && state.Status !== undefined)
		)
	}

	private findWorkflowChangedValues(previousObject: any, nextObject: any): Change[] {
		const previousMap = this.flattenWorkflowState(previousObject)
		const nextMap = this.flattenWorkflowState(nextObject)
		const keys = Array.from(new Set([...previousMap.keys(), ...nextMap.keys()]))

		return keys
			.map(key => ({
				key,
				oldValue: previousMap.get(key) ?? '-',
				newValue: nextMap.get(key) ?? '-',
			}))
			.filter(change => change.oldValue !== change.newValue)
	}

	private flattenWorkflowState(state: any): Map<string, string> {
		const values = new Map<string, string>()
		if (!state || typeof state !== 'object') return values

		this.collectWorkflowLeaves(state, [], values)
		return values
	}

	private collectWorkflowLeaves(value: any, path: string[], values: Map<string, string>) {
		if (this.shouldSkipWorkflowPath(path)) return

		if (Array.isArray(value)) {
			const summary = this.summarizeWorkflowArray(path, value)
			if (summary !== null) {
				this.setWorkflowValue(path, summary, values)
			}
			return
		}

		if (value !== null && typeof value === 'object') {
			for (const key of Object.keys(value)) {
				this.collectWorkflowLeaves(value[key], [...path, key], values)
			}
			return
		}

		this.setWorkflowValue(path, value, values)
	}

	private shouldSkipWorkflowPath(path: string[]): boolean {
		if (path.length === 0) return false

		const normalized = path.map(part => part.toLowerCase())
		const last = normalized[normalized.length - 1]

		if (normalized.includes('auditlog')) return true
		if (normalized.includes('lastvalidation')) return true
		if (last === 'dirty') return true
		if (last === 'id') return true
		if (last === 'updatedat') return true
		if (last === 'createdat') return true
		if (last === 'workflowtemplateid') return true
		if (last === 'workflowversionid') return true
		if (last === 'companyid') return true
		if (last.endsWith('id')) return true
		if (this.isDuplicateWorkflowDefinitionMetadata(normalized)) return true

		return false
	}

	private setWorkflowValue(path: string[], value: any, values: Map<string, string>) {
		const key = this.workflowDisplayKey(path)
		if (key === null) return

		const formatted = this.formatWorkflowValue(path, value)
		if (formatted === null) return

		values.set(key, formatted)
	}

	private workflowDisplayKey(path: string[]): string | null {
		const normalizedPath = this.stripWorkflowDefinitionPrefix(path)
		if (normalizedPath.length === 0) return null

		if (normalizedPath[0] === 'stages' && normalizedPath.length >= 2) {
			const stageNumber = normalizedPath[1]
			const rest = normalizedPath.slice(2)
			const area = rest[0]

			if (area === 'routing') {
				return `Stage Configuration|Stage ${stageNumber} Routing|${this.workflowFieldName(rest.slice(1))}`
			}

			if (area === 'actions') {
				return `Stage Configuration|Stage ${stageNumber} Actions|Allowed Actions`
			}

			if (area === 'rows') {
				return `Stage Configuration|Stage ${stageNumber} Fields|Form Fields`
			}

			if (area === 'hiddenInherited') {
				return `Stage Configuration|Stage ${stageNumber} Fields|Hidden Inherited Fields`
			}

			return `Stage Configuration|Stage ${stageNumber}|${this.workflowFieldName(rest)}`
		}

		if (normalizedPath[0] === 'finalOutputs') {
			const outputGroup = normalizedPath[1]
				? this.workflowGroupName(normalizedPath[1])
				: 'Output'
			const field = normalizedPath.length > 2
				? this.workflowFieldName(normalizedPath.slice(2))
				: 'Configuration'

			return `Workflow Outputs|${outputGroup}|${field}`
		}

		return `Workflow Template|Template Details|${this.workflowFieldName(normalizedPath)}`
	}

	private stripWorkflowDefinitionPrefix(path: string[]): string[] {
		if (path[0] === 'Definition' || path[0] === 'definition') {
			return path.slice(1)
		}

		return path
	}

	private workflowFieldName(path: string[]): string {
		const joined = path.join('.')
		const fieldNames: Record<string, string> = {
			Sku: 'SKU',
			sku: 'SKU',
			name: 'Name',
			Name: 'Name',
			ReferenceCode: 'Reference Code',
			referenceCode: 'Reference Code',
			description: 'Description',
			Description: 'Description',
			tier: 'Type',
			Tier: 'Type',
			status: 'Status',
			Status: 'Status',
			version: 'Version',
			Version: 'Version',
			stageCount: 'Stage Count',
			StageCount: 'Stage Count',
			activeStage: 'Active Stage',
			ActiveStage: 'Active Stage',
			InFlightCount: 'In-flight Count',
			inFlightCount: 'In-flight Count',
			PublishedAt: 'Published At',
			publishedAt: 'Published At',
			DeactivatedAt: 'Deactivated At',
			deactivatedAt: 'Deactivated At',
			actorRoles: 'Actor Roles',
			'timeBudget.unit': 'Time Budget Unit',
			'timeBudget.value': 'Time Budget',
			'approvalTarget.type': 'Approval Target',
			'approvalTarget.stage': 'Approval Stage',
			'rejectionTarget.type': 'Rejection Target',
			'rejectionTarget.stage': 'Rejection Stage',
			'conditionalRoute.enabled': 'Conditional Routing',
			'conditionalRoute.conditions': 'Conditional Conditions',
			'notifications.transition': 'Stage Transition Notification',
			'notifications.perItem': 'Per-Item Notification',
			'notifications.perItemEventLabel': 'Per-Item Event Label',
			'notifications.perItemRecipientField': 'Per-Item Recipient Field',
			'notifications.reminder': 'Reminder Notification',
			'notifications.reminderTime': 'Reminder Time',
			'notifications.reminderSchedule': 'Reminder Schedule',
			'notifications.escalation': 'Escalation Notification',
			escalationTarget: 'Escalation Target',
			immutable: 'Record Immutability',
			'retention.days': 'Retention Days',
			'retention.store': 'Store Records',
			'retention.autoPurge': 'Auto-Purge Records',
			secondApprover: 'Second Approver',
			enabled: 'Enabled',
			template: 'Template',
			recipients: 'Recipients',
		}

		return fieldNames[joined] ?? fieldNames[path[path.length - 1]] ?? this.toTitleCase(path[path.length - 1] ?? 'Value')
	}

	private summarizeWorkflowArray(path: string[], value: any[]): string | null {
		const last = path[path.length - 1]

		if (value.length === 0) return '-'
		if (last === 'actions') return this.summarizeWorkflowActions(value)
		if (last === 'rows') return this.summarizeWorkflowRows(value)
		if (last === 'conditions') return this.summarizeWorkflowConditions(value)
		if (last === 'actorRoles' || last === 'recipients' || last === 'hiddenInherited') {
			return value.map(item => this.formatWorkflowValue(path, item)).join(', ')
		}

		if (value.every(item => item === null || typeof item !== 'object')) {
			return value.map(item => this.formatWorkflowValue(path, item)).join(', ')
		}

		return this.safeJsonStringify(value)
	}

	private summarizeWorkflowActions(actions: any[]): string {
		const labels = actions
			.map(action => action?.label ?? action?.type)
			.filter(label => label !== null && label !== undefined && label !== '')

		return labels.length > 0 ? labels.join(', ') : '-'
	}

	private summarizeWorkflowRows(rows: any[]): string {
		const labels: string[] = []

		for (const row of rows) {
			const cells = Array.isArray(row) ? row : [row]
			for (const cell of cells) {
				if (cell?.label) labels.push(cell.label)
				else if (cell?.kind === 'mirror') labels.push('Inherited field')
			}
		}

		return labels.length > 0 ? labels.join(', ') : '-'
	}

	private summarizeWorkflowConditions(conditions: any[]): string {
		const labels = conditions.map(condition => {
			const field = condition?.fieldLabel ?? condition?.fieldId ?? 'Field'
			const op = condition?.op ?? 'matches'
			const value = condition?.value ?? ''
			return `${field} ${op} ${value}`.trim()
		})

		return labels.length > 0 ? labels.join('; ') : '-'
	}

	private formatWorkflowValue(path: string[], value: any): string {
		if (value === null || value === undefined || value === '') return '-'
		if (typeof value === 'boolean') return value ? 'On' : 'Off'
		if (typeof value === 'number') return String(value)

		if (typeof value === 'string') {
			return this.formatWorkflowStringValue(path, value)
		}

		return this.safeJsonStringify(value)
	}

	private formatWorkflowStringValue(path: string[], value: string): string {
		const joined = path.join('.')
		if (joined.endsWith('approvalTarget.type') || joined.endsWith('rejectionTarget.type')) {
			const targetLabels: Record<string, string> = {
				next: 'Next Stage',
				close: 'Close',
				previous: 'Previous Stage',
			}
			return targetLabels[value] ?? this.toTitleCase(value)
		}

		return value
	}

	private safeJsonStringify(value: any): string {
		try {
			return JSON.stringify(value)
		} catch {
			return String(value)
		}
	}

	private workflowGroupName(value: string): string {
		const groupNames: Record<string, string> = {
			pdf: 'PDF',
			email: 'Email',
		}

		return groupNames[value] ?? this.toTitleCase(value)
	}

	private isDuplicateWorkflowDefinitionMetadata(path: string[]): boolean {
		if (path[0] !== 'definition' || path.length !== 2) return false

		return [
			'name',
			'sku',
			'referencecode',
			'description',
			'tier',
			'status',
			'version',
			'stagecount',
			'activestage',
		].includes(path[1])
	}

	private toTitleCase(value: string): string {
		return value
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			.replace(/[_-]+/g, ' ')
			.replace(/\b\w/g, char => char.toUpperCase())
	}

	getAllKeys(obj: any, currentPath: string = ""): string[] {
		const keys: string[] = [];

		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				const newKey = currentPath ? `${currentPath}.${key}` : key;

				keys.push(newKey);

				if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
					keys.push(...this.getAllKeys(obj[key], newKey));
				} else if (Array.isArray(obj[key])) {
					obj[key].forEach((item: any, index: number) => {
						if (typeof item === "object") {
							const identifier = item.dataPointId !== undefined ? item.dataPointId : index;
							keys.push(...this.getAllKeys(item, `${newKey}[${identifier}]`));
						}
					});
				}
			}
		}

		return keys;
	}

	readKey(obj: any, keyStr: string): any {
		const keys = keyStr.split(".");
		let currentObj = obj;

		for (let key of keys) {
			if (key.includes("[")) {
				const identifier = parseInt(key.slice(key.indexOf("[") + 1, key.indexOf("]")), 10);
				const keyName = key.split("[")[0];

				const matchingObjs = currentObj[keyName].filter((item: any) => item.dataPointId === identifier);

				if (matchingObjs.length > 0) {
					currentObj = matchingObjs[0];
				} else {
					throw new Error(`Key ${keyName}[dataPointId=${identifier}] not found`);
				}

			} else {
				currentObj = currentObj[key];
			}
		}

		return currentObj;
	}

	retrieveAudits(reset?: any) {
		if (reset !== true) {
			this.currentPage = 1
		}
		const pageNumber = this.currentPage
		const pageSize = this.pageSize

		const item = {
			pageSize,
			pageNumber
		}
		this.auditService.retrieveAuditByPage(item).subscribe({
			next: (response: any) => {
				this.audits = response.auditLogs
				this.totalPages = response.totalPages
				this.loopArray = new Array(this.totalPages)
					.fill(0)
					.map((_, index) => index + 1)
			},
			error: (response) => {
				this.errorMessage = this.errorService.handleRetrievalErrors(response)
			},
		})

	}
	ngOnInit(): void {
		const userRole = localStorage.getItem('role');
		if (!isSuperAdmin(userRole)) {
			this.router.navigate(['/settings']);
			return;
		}
		const saved = this.refreshState.load<{ currentPage: number; showDetails: boolean; auditDetails: any }>('settings:audit');
		this.currentPage = Math.max(1, saved?.currentPage || 1);
		this.showDetails = Boolean(saved?.showDetails);
		this.auditDetails = saved?.auditDetails || {};
		this.retrieveAudits(true);
	}
	incrementArray(arr: any, increment: any) {
		return arr.map((item: any) => item + increment)
	}

	decrementArray(arr: any, decrement: any) {
		return arr.map((item: any) => item - decrement)
	}

	shiftPageRight() {
		const incrementedArray = this.incrementArray(
			this.loopArray,
			this.currentIncrement,
		)
		this.loopArray = incrementedArray
	}
	shiftPageLeft() {
		const decrementArray = this.decrementArray(
			this.loopArray,
			this.currentIncrement,
		)
		this.loopArray = decrementArray
	}
	setCurrentPage(page: any) {
		this.currentPage = page
		this.persistRefreshState()
		this.retrieveAudits(true)
	}
	performIncrement() {
		if (this.currentPage === this.totalPages) return
		const result = this.currentPage + 1
		this.setCurrentPage(result)
		if (this.currentPage > this.loopArray[this.loopArray.length - 1]) {
			this.shiftPageRight()
		}
	}

	performDecrement() {
		if (this.currentPage === 1) return
		const result = this.currentPage - 1
		this.setCurrentPage(result)
		if (this.currentPage < this.loopArray[0]) {
			this.shiftPageLeft()
		}
	}

	ngOnDestroy(): void {
		this.persistRefreshState()
	}

	private persistRefreshState(): void {
		this.refreshState.save('settings:audit', {
			currentPage: this.currentPage,
			showDetails: this.showDetails,
			auditDetails: this.auditDetails,
		})
	}

}
